import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Check, Crown, Sparkles, Image, Mic, FileText, ArrowLeft, Zap, Users } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useRazorpay } from '@/hooks/useRazorpay';
import { useNavigate } from 'react-router-dom';
import { PRICING_PLANS } from '@/constants/pricing';
import SEOHead from '@/components/SEOHead';
import StructuredData, { faqSchema, breadcrumbSchema } from '@/components/StructuredData';

const Pricing = () => {
  const { user } = useAuth();
  const { createSubscription, isProcessing } = useRazorpay();
  const navigate = useNavigate();

  const plans = [
    {
      ...PRICING_PLANS.free,
      period: 'month',
      limitations: [
        'Limited to 5 generations total',
        '512px image resolution',
        'Watermarked content',
        'Community support only'
      ],
      color: 'border-border',
      buttonColor: 'bg-transparent border border-border text-foreground hover:bg-accent',
      icon: <Sparkles className="w-6 h-6 text-emerald-600" />,
      popular: false
    },
    {
      ...PRICING_PLANS.starter,
      period: 'month',
      color: 'border-emerald-500',
      buttonColor: 'bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white',
      icon: <Zap className="w-6 h-6 text-emerald-600" />,
      popular: false,
      limitations: undefined
    },
    {
      ...PRICING_PLANS.pro,
      period: 'month',
      color: 'border-purple-500 ring-2 ring-purple-200 dark:ring-purple-800',
      buttonColor: 'bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white',
      icon: <Crown className="w-6 h-6 text-purple-600" />,
      popular: true,
      limitations: undefined
    },
    {
      ...PRICING_PLANS.business,
      period: 'month',
      color: 'border-blue-500',
      buttonColor: 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white',
      icon: <Users className="w-6 h-6 text-blue-600" />,
      popular: false,
      limitations: undefined
    }
  ];

  const handleSubscribe = async (planId: string) => {
    if (!user) {
      navigate('/dashboard');
      return;
    }

    if (planId === 'free') {
      navigate('/dashboard');
      return;
    }

    // Handle paid plans (starter, pro, team)
    if (planId === 'starter' || planId === 'pro' || planId === 'team') {
      try {
        await createSubscription(planId as 'starter' | 'pro' | 'team');
      } catch (error) {
        console.error('Subscription error:', error);
      }
    }
  };

  const getButtonText = (planId: string) => {
    if (user?.plan === planId) {
      return 'Current Plan';
    }
    if (planId === 'free') {
      return 'Get Started Free';
    }
    return `Subscribe to ${PRICING_PLANS[planId as keyof typeof PRICING_PLANS]?.name || planId}`;
  };

  const pricingFaqs = [
    { question: 'What happens when I reach my limit?', answer: 'You can upgrade to a higher plan anytime. Limits reset at the start of each billing cycle.' },
    { question: 'Can I cancel my subscription anytime?', answer: 'Yes, you can cancel your subscription at any time from your account settings. No long-term commitments required.' },
    { question: 'Do unused credits roll over?', answer: 'No, usage limits reset monthly and don\'t carry over to the next billing cycle.' },
    { question: 'Can I upgrade or downgrade?', answer: 'Yes! You can change your plan anytime. When upgrading, your new limits take effect immediately.' },
  ];

  const pricingBreadcrumbs = [
    { name: 'Home', url: 'https://genova-ai-studio.lovable.app/' },
    { name: 'Pricing', url: 'https://genova-ai-studio.lovable.app/pricing' },
  ];

  return (
    <>
      <SEOHead
        title="Pricing Plans - Genova AI Studio | Affordable AI Content Creation"
        description="Choose the perfect AI content creation plan. Free, Starter (₹499), Pro (₹1,199), or Business (₹2,499). Generate AI images, voice & scripts. Start free!"
        keywords="AI content creation pricing, AI image generator plans, affordable AI tools, Genova AI pricing, AI voice generator price, AI script writer cost"
        canonicalUrl="https://genova-ai-studio.lovable.app/pricing"
      />
      <StructuredData data={faqSchema(pricingFaqs)} id="pricing-faq-schema" />
      <StructuredData data={breadcrumbSchema(pricingBreadcrumbs)} id="pricing-breadcrumb" />
    <main className="min-h-screen bg-background">
      <article className="container mx-auto px-4 py-16">
        {/* Go Back Button */}
        <nav className="mb-8" aria-label="Back navigation">
          <Button
            onClick={() => navigate(-1)}
            variant="outline"
            className="flex items-center space-x-2"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Go Back</span>
          </Button>
        </nav>

        {/* Header */}
        <header className="text-center mb-16">
          <h1 className="text-5xl font-bold bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent mb-4">
            AI Content Creation Plans & Pricing
          </h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Unlock the power of AI for image generation, voice synthesis, and script creation. 
            Perfect for creators, businesses, and teams. Start free today.
          </p>
        </header>

        {/* Feature Highlights */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
          <div className="flex items-center justify-center p-6 bg-card rounded-xl shadow-sm border border-border">
            <Image className="w-8 h-8 text-purple-600 mr-3" />
            <div>
              <h3 className="font-semibold text-card-foreground">AI Image Generation</h3>
              <p className="text-sm text-muted-foreground">Create stunning visuals</p>
            </div>
          </div>
          <div className="flex items-center justify-center p-6 bg-card rounded-xl shadow-sm border border-border">
            <Mic className="w-8 h-8 text-blue-600 mr-3" />
            <div>
              <h3 className="font-semibold text-card-foreground">AI Voice Synthesis</h3>
              <p className="text-sm text-muted-foreground">Generate realistic voiceovers</p>
            </div>
          </div>
          <div className="flex items-center justify-center p-6 bg-card rounded-xl shadow-sm border border-border">
            <FileText className="w-8 h-8 text-green-600 mr-3" />
            <div>
              <h3 className="font-semibold text-card-foreground">AI Script Writing</h3>
              <p className="text-sm text-muted-foreground">Create engaging content</p>
            </div>
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
          {plans.map((plan) => (
            <Card key={plan.id} className={`relative ${plan.color} hover:shadow-xl transition-all duration-200`}>
              {plan.popular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <Badge className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-4 py-1 font-bold">
                    Most Popular
                  </Badge>
                </div>
              )}
              
              <CardHeader className="text-center pb-4">
                <div className="flex items-center justify-center mb-4">
                  {plan.icon}
                </div>
                <CardTitle className="text-2xl font-bold text-card-foreground">{plan.name}</CardTitle>
                <div className="mt-4">
                  <span className="text-4xl font-bold text-foreground">{plan.price}</span>
                  <span className="text-muted-foreground ml-2 text-base">/{plan.period}</span>
                </div>
                <p className="text-muted-foreground mt-3 text-sm leading-relaxed">{plan.description}</p>
              </CardHeader>
              
              <CardContent className="space-y-6">
                <div className="space-y-3">
                  {plan.features.map((feature, index) => (
                    <div key={index} className="flex items-start">
                      <Check className="w-5 h-5 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-muted-foreground leading-relaxed">{feature}</span>
                    </div>
                  ))}
                </div>

                {plan.limitations && (
                  <div className="border-t border-border pt-4 mt-4">
                    <p className="text-xs text-muted-foreground mb-2 font-medium">Limitations:</p>
                    {plan.limitations.map((limitation, index) => (
                      <p key={index} className="text-xs text-muted-foreground mb-1">• {limitation}</p>
                    ))}
                  </div>
                )}
                
                <Button
                  onClick={() => handleSubscribe(plan.id)}
                  disabled={isProcessing || user?.plan === plan.id}
                  className={`w-full py-3 text-base font-semibold rounded-lg transition-all duration-200 shadow-md hover:shadow-lg ${plan.buttonColor} border-0`}
                >
                  {isProcessing ? (
                    <>
                      <Sparkles className="w-4 h-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    getButtonText(plan.id)
                  )}
                </Button>

                {user?.plan === plan.id && (
                  <div className="text-center mt-4">
                    <Badge variant="secondary" className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100 font-medium">
                      Current Plan
                    </Badge>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Plan Comparison Table */}
        <div className="mt-20 max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-8 text-foreground">Plan Comparison</h2>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-4 px-4 font-semibold text-foreground">Feature</th>
                  <th className="text-center py-4 px-4 font-semibold text-foreground">Free</th>
                  <th className="text-center py-4 px-4 font-semibold text-foreground">Starter</th>
                  <th className="text-center py-4 px-4 font-semibold text-purple-600">Pro</th>
                  <th className="text-center py-4 px-4 font-semibold text-foreground">Team</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-border">
                  <td className="py-3 px-4 text-muted-foreground">Monthly Images</td>
                  <td className="text-center py-3 px-4">5</td>
                  <td className="text-center py-3 px-4">20</td>
                  <td className="text-center py-3 px-4 font-semibold text-purple-600">40</td>
                  <td className="text-center py-3 px-4">100</td>
                </tr>
                <tr className="border-b border-border">
                  <td className="py-3 px-4 text-muted-foreground">Monthly Scripts</td>
                  <td className="text-center py-3 px-4">5</td>
                  <td className="text-center py-3 px-4">20</td>
                  <td className="text-center py-3 px-4 font-semibold text-purple-600">40</td>
                  <td className="text-center py-3 px-4">100</td>
                </tr>
                <tr className="border-b border-border">
                  <td className="py-3 px-4 text-muted-foreground">Voice Minutes</td>
                  <td className="text-center py-3 px-4">20 sec</td>
                  <td className="text-center py-3 px-4">20 min</td>
                  <td className="text-center py-3 px-4 font-semibold text-purple-600">40 min</td>
                  <td className="text-center py-3 px-4">100 min</td>
                </tr>
                <tr className="border-b border-border">
                  <td className="py-3 px-4 text-muted-foreground">Image Quality</td>
                  <td className="text-center py-3 px-4">512px</td>
                  <td className="text-center py-3 px-4">1024px</td>
                  <td className="text-center py-3 px-4 font-semibold text-purple-600">1536px</td>
                  <td className="text-center py-3 px-4">2048px</td>
                </tr>
                <tr className="border-b border-border">
                  <td className="py-3 px-4 text-muted-foreground">Watermark</td>
                  <td className="text-center py-3 px-4">Yes</td>
                  <td className="text-center py-3 px-4">Reduced</td>
                  <td className="text-center py-3 px-4 font-semibold text-purple-600">No</td>
                  <td className="text-center py-3 px-4">No</td>
                </tr>
                <tr className="border-b border-border">
                  <td className="py-3 px-4 text-muted-foreground">Support</td>
                  <td className="text-center py-3 px-4">Community</td>
                  <td className="text-center py-3 px-4">Email</td>
                  <td className="text-center py-3 px-4 font-semibold text-purple-600">Priority</td>
                  <td className="text-center py-3 px-4">Priority</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="mt-20 max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12 text-foreground">Frequently Asked Questions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-card p-6 rounded-lg shadow-sm border border-border">
              <h3 className="font-semibold mb-3 text-card-foreground">What happens when I reach my limit?</h3>
              <p className="text-muted-foreground text-sm leading-relaxed">You can upgrade to a higher plan anytime. Limits reset at the start of each billing cycle.</p>
            </div>
            <div className="bg-card p-6 rounded-lg shadow-sm border border-border">
              <h3 className="font-semibold mb-3 text-card-foreground">Can I cancel my subscription anytime?</h3>
              <p className="text-muted-foreground text-sm leading-relaxed">Yes, you can cancel your subscription at any time from your account settings. No long-term commitments required.</p>
            </div>
            <div className="bg-card p-6 rounded-lg shadow-sm border border-border">
              <h3 className="font-semibold mb-3 text-card-foreground">Do unused credits roll over?</h3>
              <p className="text-muted-foreground text-sm leading-relaxed">No, usage limits reset monthly and don't carry over to the next billing cycle.</p>
            </div>
            <div className="bg-card p-6 rounded-lg shadow-sm border border-border">
              <h3 className="font-semibold mb-3 text-card-foreground">Can I upgrade or downgrade?</h3>
              <p className="text-muted-foreground text-sm leading-relaxed">Yes! You can change your plan anytime. When upgrading, your new limits take effect immediately.</p>
            </div>
          </div>
        </div>
      </article>
    </main>
    </>
  );
};

export default Pricing;
